import { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../config';
import { useAuth } from '../context/AuthContext';

export interface OnboardingStatus {
  needsOnboarding: boolean;
  loading: boolean;
  error: boolean;
  favoritesCount: number;
  portfolioCount: number;
  refresh: () => Promise<void>;
}

export const useOnboardingStatus = (): OnboardingStatus => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [needsOnboarding, setNeedsOnboarding] = useState<boolean>(false);
  const [favoritesCount, setFavoritesCount] = useState<number>(0);
  const [portfolioCount, setPortfolioCount] = useState<number>(0);

  const checkStatus = useCallback(async () => {
    if (!token || !user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(false);

      // We need to check both favorites and portfolio
      const [favsRes, portRes] = await Promise.all([
        fetch(`${API_URL}/api/favorites`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/api/portfolio`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (favsRes.ok && portRes.ok) {
        const favs = await favsRes.json();
        const port = await portRes.json();

        const fCount = Array.isArray(favs) ? favs.length : 0;
        const pCount = Array.isArray(port) ? port.length : 0;

        setFavoritesCount(fCount);
        setPortfolioCount(pCount);
        
        // Onboarding needed if BOTH are empty
        setNeedsOnboarding(fCount === 0 && pCount === 0);
      } else {
        setError(true);
      }
    } catch (err) {
      console.error('Error checking onboarding status:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  return {
    needsOnboarding,
    loading,
    error,
    favoritesCount,
    portfolioCount,
    refresh: checkStatus,
  };
};
